import { isValidAddress, isValidAddressDomain } from '../utils/smtpAddress';
/* tslint:disable:forbid-import */
// OutlookWeb:24136
import { action, observable } from 'mobx';
/* tslint:enable:forbid-import */
import { observer } from 'mobx-react';
import { IconButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { FocusZone } from 'office-ui-fabric-react/lib/FocusZone';
import { List } from 'office-ui-fabric-react/lib/List';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { assertNever } from 'owa-assert';
import { ControlIcons } from 'owa-control-icons';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import strings from 'owa-strings/owa-options-strings';
import { trace } from 'owa-trace';
import * as React from 'react';
import {
    IObjectWithKey,
    Selection,
    SelectionMode,
    SelectionZone,
} from 'office-ui-fabric-react/lib/Selection';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';

let styles = require('./EditSendersAndDomains.scss');
let classNames = require('classnames/bind');

export const enum EditSendersAndDomainsMode {
    EmailAddressOnly,
    DomainOnly,
    EmailAddressAndDomain,
}

export interface EditSendersAndDomainsProps {
    items: string[];
    onItemsChanged: (items: string[]) => void;
    mode: EditSendersAndDomainsMode;
}

const enum ValidationResult {
    OK,
    InvalidAddress,
    InvalidAddressOrDomain,
    InvalidDomain,
    IsSelf,
}

/**
 * Holds the controlled state of a list item being edited
 */
interface EditItemState {
    index: number; // index of the item being edited
    errorMessage: string | null; // displayed if  item is invalid on save
    value: string | null; // current value of controlled edit item text field
}

/**
 * Holds the controlled state of the new item text field
 */
interface NewItemState {
    errorMessage: string | null; // displayed if  new item is invalid on save
    value: string | null; // current value of controlled new item text field
}

@observer
export class EditSendersAndDomains extends React.Component<EditSendersAndDomainsProps, {}> {
    @observable private editItemState: EditItemState | null = null;
    @observable private newItemState: NewItemState = { errorMessage: null, value: null };
    @observable private selection = null;
    private hasMounted: boolean;

    constructor(props: EditSendersAndDomainsProps) {
        super(props);
        this.resetSelection(this.props.items);
    }

    public componentDidMount(): void {
        this.hasMounted = true;
    }

    public componentWillUpdate(nextProps: EditSendersAndDomainsProps, nextState: {}): void {
        if (!this.selection) {
            this.resetSelection(nextProps.items);
        }
    }

    public render(): JSX.Element {
        const itemsWithKey = this.props.items.map(item => {
            return { key: item };
        });

        return (
            <div>
                <div>
                    <IconButton
                        className={styles.editListItemButton}
                        data-is-focusable={true}
                        disabled={this.selection.count !== 1}
                        iconProps={{ iconName: ControlIcons.Edit }}
                        onClick={this.onEditListItemButtonClicked}
                        styles={{
                            rootDisabled: styles.disabledButton,
                        }}
                        title={strings.options_common_editEmailAddressOrDomainButtonTitle}
                    />
                    <IconButton
                        className={styles.iconButton}
                        data-is-focusable={true}
                        disabled={!this.selection.count || this.selection.count === 0}
                        iconProps={{ iconName: ControlIcons.Delete }}
                        onClick={this.onDeleteListItemButtonClicked}
                        styles={{
                            rootDisabled: styles.disabledButton,
                        }}
                        title={strings.options_common_removeEmailAddressOrDomainButtonTitle}
                    />
                </div>
                <div className={styles.outerContainer}>
                    <div className={styles.textFieldAndListContainer}>
                        <div>
                            <TextField
                                className={styles.textField}
                                onChanged={this.onNewItemTextFieldChanged}
                                onFocus={this.onNewItemTextFieldFocus}
                                onKeyDown={this.onNewItemTextFieldKeyDown}
                                placeholder={this.newItemTextFieldPlaceHolderText()}
                                value={this.newItemState.value || ''}
                            />
                        </div>
                        {this.newItemState.errorMessage && (
                            <MessageBar
                                isMultiline={false}
                                messageBarType={MessageBarType.error}
                                onDismiss={this.clearNewItemTextFieldErrorMessage}>
                                {this.newItemState.errorMessage}
                            </MessageBar>
                        )}
                        <FocusZone>
                            <SelectionZone
                                onItemInvoked={this.onItemInvoked}
                                selection={this.selection}
                                selectionMode={SelectionMode.multiple}>
                                <div className={styles.listItemContainer} data-is-scrollable={true}>
                                    <List items={itemsWithKey} onRenderCell={this.renderListItem} />
                                </div>
                            </SelectionZone>
                        </FocusZone>
                    </div>
                    <PrimaryButton
                        text={strings.options_common_addButtonLabel}
                        data-is-focusable={true}
                        disabled={!this.newItemState.value || this.newItemState.value.length === 0}
                        onClick={this.onAddNewItemButtonClicked}
                        className={styles.addNewItemButton}
                    />
                </div>
            </div>
        );
    }

    private renderListItem = (item: IObjectWithKey, index: number) => {
        const isEdit = this.editItemState && this.editItemState.index === index;
        const isSelected = this.selection.isIndexSelected(index);
        return (
            <div
                data-selection-index={index}
                key={index}
                data-is-focusable={!isEdit}
                data-selection-disabled={isEdit}>
                {isEdit
                    ? this.renderEditListItem(item, index)
                    : this.renderViewListItem(item, index, isSelected)}
            </div>
        );
    };

    private renderViewListItem = (item: IObjectWithKey, index: number, isSelected: boolean) => {
        const itemClassName = classNames(
            styles.viewListItemCell,
            isSelected && styles.viewListItemCellSelected
        );
        return (
            <div
                className={itemClassName}
                data-is-focusable={true}
                key={index}
                onKeyDown={this.onViewListItemTextFieldKeyDown}>
                {item.key as string}
            </div>
        );
    };

    private renderEditListItem = (item: IObjectWithKey, index: number) => {
        this.selection.setChangeEvents(false);
        return (
            <div key={index} data-is-focusable={false}>
                <TextField
                    data-is-focusable={false}
                    className={styles.textField}
                    onBlur={() => this.onEditItemTextFieldBlur(index)}
                    onChanged={this.onEditItemTextFieldChanged}
                    onFocus={this.moveEditItemTextFieldCaretToEnd}
                    onKeyDown={event => this.onEditItemTextFieldKeyDown(event, index)}
                    componentRef={input => {
                        input && this.shouldSetFocusInEditListItemTextField() && input.focus();
                    }}
                    value={this.editItemState.value}
                />
                {this.editItemState.errorMessage && (
                    <MessageBar isMultiline={false} messageBarType={MessageBarType.error}>
                        {this.editItemState.errorMessage}
                    </MessageBar>
                )}
            </div>
        );
    };

    private shouldSetFocusInEditListItemTextField(): boolean {
        // only set focus when initially displaying the text field and not
        // when there is an error message displayed - otherwise focus is trapped
        // in the text field when there is an error message
        return !this.editItemState.errorMessage;
    }

    @action
    private onAddNewItemButtonClicked = () => {
        if (this.isDuplicate(this.newItemState.value)) {
            this.newItemState.value = '';
            return;
        }
        const result = this.validateSenderOrDomain(this.newItemState.value);
        if (result == ValidationResult.OK) {
            const sendersAndDomains = this.props.items.slice(0);
            sendersAndDomains.unshift(this.newItemState.value.trim());
            this.props.onItemsChanged(sendersAndDomains);
            this.newItemState.value = '';
        } else {
            this.setNewItemTextFieldErrorMessage(this.validationErrorMessage(result));
        }
    };

    @action
    private onEditItemTextFieldBlur = (index: number) => {
        const result = this.validateSenderOrDomain(this.editItemState.value);
        if (result == ValidationResult.OK) {
            const sendersAndDomains = this.props.items.slice(0);
            sendersAndDomains.splice(index, 1, this.editItemState.value.trim());
            this.props.onItemsChanged(sendersAndDomains);
            this.editItemState = null;
            this.selection = null;
        } else {
            this.setEditItemTextFieldErrorMessage(this.validationErrorMessage(result));
        }
    };

    @action
    private resetSelectionOnNextRender = () => {
        this.selection = null;
    };

    @action
    private resetSelection = items => {
        this.selection = new Selection({
            onSelectionChanged: () => {
                if (this.hasMounted) {
                    this.forceUpdate();
                }
            },
            getKey: (item, index) => index.toString(),
        });
        const itemsWithKey = items.map(item => {
            return { key: item };
        });
        this.selection.setItems(itemsWithKey, false);
    };

    @action
    private onEditItemTextFieldChanged = newValue => {
        this.clearEditItemTextFieldErrorMessage();
        this.editItemState.value = newValue;
    };

    @action
    private onEditItemTextFieldKeyDown = (
        event: React.KeyboardEvent<EventTarget>,
        index: number
    ) => {
        switch (event.keyCode) {
            case KeyboardCharCodes.Enter:
                const result = this.validateSenderOrDomain(this.editItemState.value);
                if (result == ValidationResult.OK) {
                    const sendersAndDomains = this.props.items.slice(0);
                    sendersAndDomains.splice(index, 1, this.editItemState.value.trim());
                    this.props.onItemsChanged(sendersAndDomains);
                    this.editItemState = null;
                    this.resetSelectionOnNextRender();
                } else {
                    this.setEditItemTextFieldErrorMessage(this.validationErrorMessage(result));
                }
                break;
            case KeyboardCharCodes.Esc:
                event.stopPropagation();
                this.editItemState = null;
                this.resetSelectionOnNextRender();
                break;
            default:
                break;
        }
    };

    @action
    private onEditListItemButtonClicked = () => {
        if (this.selection.count != 1) {
            throw new Error('onEditListItemButtonClicked with selected items count != 1');
        }
        const item = this.selection.getItems()[0];
        const index = this.props.items.indexOf(item.key as string);
        this.editItemState = {
            value: item.key as string,
            index: index,
            errorMessage: null,
        };
    };

    @action
    private onDeleteListItemButtonClicked = () => {
        const sendersAndDomains = this.props.items.slice(0);
        this.selection.getSelection().forEach(item => {
            const index = sendersAndDomains.indexOf(item.key as string);
            if (index !== -1) {
                sendersAndDomains.splice(index, 1);
            }
        });
        this.props.onItemsChanged(sendersAndDomains);
        this.resetSelectionOnNextRender();
    };

    @action
    private onItemInvoked = (invokedItem: any) => {
        if (
            (this.editItemState && this.editItemState.errorMessage) ||
            this.newItemState.errorMessage
        ) {
            return;
        }

        const index = this.props.items.indexOf(invokedItem.key);
        if (index !== -1) {
            this.selection.setAllSelected(false);
            this.editItemState = {
                value: invokedItem.key as string,
                index: index,
                errorMessage: null,
            };
        }
    };

    @action
    private onNewItemTextFieldChanged = text => {
        this.clearNewItemTextFieldErrorMessage();
        this.newItemState.value = text;
    };

    private onNewItemTextFieldFocus = () => {
        this.selection.setAllSelected(false);
    };

    private onNewItemTextFieldKeyDown = (event: React.KeyboardEvent<EventTarget>) => {
        switch (event.keyCode) {
            case KeyboardCharCodes.Enter:
                if (this.newItemState.value && this.newItemState.value.length > 0) {
                    const result = this.validateSenderOrDomain(this.newItemState.value);
                    if (result == ValidationResult.OK) {
                        this.onAddNewItemButtonClicked();
                    } else {
                        this.setNewItemTextFieldErrorMessage(this.validationErrorMessage(result));
                    }
                }
                break;
            case KeyboardCharCodes.Esc:
                event.stopPropagation();
                break;
            default:
                break;
        }
    };

    private onViewListItemTextFieldKeyDown = (event: React.KeyboardEvent<EventTarget>) => {
        switch (event.keyCode) {
            case KeyboardCharCodes.Backspace:
            case KeyboardCharCodes.Delete:
                this.onDeleteListItemButtonClicked();
                break;
            default:
                break;
        }
    };

    private moveEditItemTextFieldCaretToEnd = event => {
        var tempValue = event.target.value;
        event.target.value = '';
        event.target.value = tempValue;
    };

    private newItemTextFieldPlaceHolderText = () => {
        switch (this.props.mode) {
            case EditSendersAndDomainsMode.DomainOnly:
                return strings.options_common_addDomainPlaceholder;
            case EditSendersAndDomainsMode.EmailAddressAndDomain:
                return strings.options_common_addSenderOrDomainPlaceholder;
            case EditSendersAndDomainsMode.EmailAddressOnly:
                return strings.options_common_addSenderPlaceholder;
            default:
                return assertNever(this.props.mode);
        }
    };

    private isSelf = (smtpAddress: string) => {
        const sessionSettings = getUserConfiguration().SessionSettings;
        const userEmailAddress = sessionSettings.UserEmailAddress;
        const lowerCaseSmtpAddress = smtpAddress.toLowerCase();
        if (!userEmailAddress) {
            trace.error('User email address from session data is null.');
        }
        return (
            userEmailAddress.toLowerCase() === lowerCaseSmtpAddress ||
            (sessionSettings.UserProxyAddresses &&
                sessionSettings.UserProxyAddresses.some(
                    address => address.toLowerCase() === lowerCaseSmtpAddress
                ))
        );
    };

    private isDuplicate(text: string) {
        // Assume both local part and domain are case-insensitive
        const lowerCaseText = text.toLowerCase();
        return this.props.items.some(sender => sender.toLowerCase() === lowerCaseText);
    }

    @action
    private setEditItemTextFieldErrorMessage = (message: string) => {
        this.editItemState.errorMessage = message;
        this.forceUpdate();
    };

    @action
    private setNewItemTextFieldErrorMessage = (message: string) => {
        this.newItemState.errorMessage = message;
    };

    @action
    private clearEditItemTextFieldErrorMessage = () => {
        this.editItemState.errorMessage = null;
        this.forceUpdate();
    };

    @action
    private clearNewItemTextFieldErrorMessage = () => {
        this.newItemState.errorMessage = null;
    };

    private validateSenderOrDomain = (text: string) => {
        const trimmedText = text.trim();
        switch (this.props.mode) {
            case EditSendersAndDomainsMode.DomainOnly:
                return this.validateDomain(trimmedText);
            case EditSendersAndDomainsMode.EmailAddressOnly:
                return this.validateEmailAddress(trimmedText);
            case EditSendersAndDomainsMode.EmailAddressAndDomain:
                if (
                    this.validateEmailAddress(trimmedText) == ValidationResult.OK ||
                    this.validateDomain(trimmedText) == ValidationResult.OK
                ) {
                    return ValidationResult.OK;
                } else {
                    return ValidationResult.InvalidAddressOrDomain;
                }
            default:
                return assertNever(this.props.mode);
        }
    };

    private validateEmailAddress = (text: string) => {
        if (this.isSelf(text)) {
            return ValidationResult.IsSelf;
        }
        return isValidAddress(text) ? ValidationResult.OK : ValidationResult.InvalidAddress;
    };

    private validateDomain = (text: string) => {
        return isValidAddressDomain(text) ? ValidationResult.OK : ValidationResult.InvalidDomain;
    };

    private validationErrorMessage = (result: ValidationResult) => {
        switch (result) {
            case ValidationResult.OK:
                throw new Error('validationErrorMessage called for OK');
            case ValidationResult.InvalidAddress:
                return strings.options_common_invalidAddressError;
            case ValidationResult.InvalidAddressOrDomain:
                return strings.options_common_invalidAddressOrDomainError;
            case ValidationResult.InvalidDomain:
                return strings.options_common_invalidDomainError;
            case ValidationResult.IsSelf:
                return strings.options_common_invalidIsSelfError;
            default:
                return assertNever(result);
        }
    };
}
